import 'ag-grid-community/styles/ag-grid.css'; // Core CSS
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Theme
import { Col, Layout, notification, Row, Skeleton, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetClientAssigned } from 'src/app/features/feature-transaction-rules/all-transactions/index.hook';
import NewCommonTable from 'src/app/shared/components/common/new-table';
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider';
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model';
import { useDefaultSearchData } from 'src/app/shared/components/common/table/table.constants';
import { ShortenListTags } from 'src/app/shared/components/common/tag/ShortenListTags';
import { STATUS_CODES } from 'src/app/shared/constants/common.constants';
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants';
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants';
import { IClient } from 'src/app/shared/hooks/useClient';
import usePrevious from 'src/app/shared/hooks/usePrevious';
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission';
import { SUMMARY_TYPE } from 'src/app/shared/models/Exception.model';
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model';
import { getExceptionProcessing, makeAchExceptionDecision, makeCheckExceptionDecision } from 'src/app/shared/services/TransactionService';
import { formatNumberWithCommas } from 'src/app/shared/utils';
import { QuickExceptionSetTabs } from '.';
import DecisionModal from './components/DecisionModal';
import DefaultDecisionTag from './components/DefaultDecisionTag';
import SearchArea from './components/SearchArea';
import StatusTag from './components/StatusTag';
import SummaryCard from './components/SummaryCard';
import ViewExceptionDetailModal from './components/ViewExceptionDetailModal';
import { DECISION_CODE, DECISION_TYPE, TRANSACTION_TYPE } from './components/constants';
import { EXCEPTION_DETAIL_DEFAULT } from './constant';
import './index.scss';
import { formatData } from './utils';

function QuickExceptionProcessing(props: {
  readonly type: QuickExceptionSetTabs;
  readonly onDecisionsNeededChange?: (total: number) => void;
  readonly onAvailableServicesChange?: (services: { hasAch: boolean; hasCheck: boolean }) => void;
  readonly selectedClients: any[];
  readonly selectedAccounts: any[];
  readonly setSelectedClients: (clients: any[]) => void;
  readonly setSelectedAccounts: (accounts: any[]) => void;
  readonly clients: Array<IClient>;
  readonly loadingClients: boolean;
  readonly handleGetClients: (considerActivationDate?: boolean) => Promise<any>;
  readonly handleGetClientInfoByUser: () => Promise<any>;
}) {
  const numberColumns = useMemo(() => ['amount', 'checkNumber', 'issuedAmount'], [])
  const dateColumns = useMemo(
    () => ['exceptionDate', 'decisionTime', 'decisionDueDate', 'paidDate', 'issuedDate', 'transactionCode'],
    [],
  )
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isPaying, setIsPaying] = useState(false)
  const [isReturning, setIsReturning] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [rowData, setRowData] = useState<any>([])
  const [total, setTotal] = useState(0)
  const [summaryInfo, setSummaryInfo] = useState<any>()
  const [combinedSummaryInfo, setCombinedSummaryInfo] = useState<any>()
  const [isExceptionDetailModalOpen, setIsExceptionDetailModalOpen] = useState(false)

  const selectedClients = props.selectedClients
  const selectedAccounts = props.selectedAccounts
  const setSelectedClients = props.setSelectedClients
  const setSelectedAccounts = props.setSelectedAccounts
  const [selectedSummaryType, setSelectedSummaryType] = useState<SUMMARY_TYPE | null>(null)

  const defaultSearchData = useDefaultSearchData()

  const { userPermissions } = useRolePermission()
  const [exceptionDetail, setExceptionDetail] = useState(EXCEPTION_DETAIL_DEFAULT)

  const { clients, loadingClients, handleGetClients, handleGetClientInfoByUser } = props

  const { accounts, handleGetAccounts, loadingAccounts } = useGetClientAssigned()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [api, contextHolder] = notification.useNotification()

  const updateBadgeCount = useCallback(
    async (payload: any) => {
      try {
        if (selectedClients?.length && selectedAccounts?.length && props.onDecisionsNeededChange) {
          const response = await getExceptionProcessing({
            ...payload,
            clientIds: selectedClients?.includes(null) ? null : selectedClients,
            accountIds: selectedAccounts?.includes(null) ? null : selectedAccounts,
            exceptionSummaryType: null, // Always fetch unfiltered count for badge
            types: [props.type],
            applyBlankIssuedFields: true
          })
          const { quickExceptionItems, ...rest } = response?.data?.items
          if (rest.decisionsNeededTotal !== undefined) {
            props.onDecisionsNeededChange(rest.decisionsNeededTotal)
          }
        }
      } catch (error) {
        if (props.onDecisionsNeededChange) {
          props.onDecisionsNeededChange(0)
        }
      }
    },
    [selectedClients, selectedAccounts, props.type, props.onDecisionsNeededChange],
  )

  const getExceptionProcessingData = useCallback(
    async (payload: any) => {
      try {
        if (selectedClients?.length && selectedAccounts?.length) {
          setIsLoading(true)
          const response = await getExceptionProcessing({
            ...payload,
            clientIds: selectedClients?.includes(null) ? null : selectedClients,
            accountIds: selectedAccounts?.includes(null) ? null : selectedAccounts,
            exceptionSummaryType: getExceptionStatus(selectedSummaryType),
            types: [props.type],
            applyBlankIssuedFields: true
          })
          const { quickExceptionItems, ...rest } = response?.data?.items
          setSummaryInfo(rest)
          setRowData(formatData(quickExceptionItems))
          setTotal(response?.data?.total)
          setIsFirstLoad(false)
          return
        }
      } catch (error) {
        setRowData([])
        setSummaryInfo({
          transactionTotal: 0,
          transactionAmount: 0,
          decisionedTotal: 0,
          decisionedAmount: 0,
          decisionsNeededTotal: 0,
          decisionsNeededAmount: 0,
        })
      } finally {
        setIsLoading(false)
      }
    },
    [selectedClients, selectedAccounts, props.type, selectedSummaryType],
  )

  const getCombinedSummaryData = useCallback(
    async (payload: any) => {
      try {
        if (selectedClients?.length && selectedAccounts?.length) {
          const response = await getExceptionProcessing({
            ...payload,
            clientIds: selectedClients?.includes(null) ? null : selectedClients,
            accountIds: selectedAccounts?.includes(null) ? null : selectedAccounts,
            exceptionSummaryType: null, // Always fetch all data for summary cards
            types: [QuickExceptionSetTabs.Ach, QuickExceptionSetTabs.Check],
            applyBlankIssuedFields: true
          })
          const { quickExceptionItems, ...rest } = response?.data?.items
          setCombinedSummaryInfo(rest)
          return
        }
      } catch (error) {
        setCombinedSummaryInfo({
          transactionTotal: 0,
          transactionAmount: 0,
          decisionedTotal: 0,
          decisionedAmount: 0,
          decisionsNeededTotal: 0,
          decisionsNeededAmount: 0,
        })
      }
    },
    [selectedClients, selectedAccounts],
  )

  const handleSearch = useCallback(
    async (payload: Partial<any>) => {
      const isTableSearch = payload.searchTerm && payload.searchTerm.trim() !== ''
      await getExceptionProcessingData(payload)
      
      if (!isTableSearch) {
        await Promise.all([
          getCombinedSummaryData(defaultSearchData),
          updateBadgeCount(defaultSearchData)
        ])
      }
    },
    [getExceptionProcessingData, getCombinedSummaryData, updateBadgeCount, defaultSearchData],
  )

  const refreshSummaryAndBadges = useCallback(
    async () => {
      await Promise.all([
        getCombinedSummaryData(defaultSearchData),
        updateBadgeCount(defaultSearchData)
      ])
    },
    [getCombinedSummaryData, updateBadgeCount, defaultSearchData],
  )

  const handleDownloadFunc = async (payload: any) =>
    await getExceptionProcessing({
      ...payload,
      clientIds: selectedClients?.includes(null) ? null : selectedClients,
      accountIds: selectedAccounts?.includes(null) ? null : selectedAccounts,
      exceptionSummaryType: getExceptionStatus(selectedSummaryType),
      types: [props.type],
    })

  const previousSelectedAccounts = usePrevious(selectedAccounts)

  const handleExceptionStatusSearch = (type: SUMMARY_TYPE) => {
    if (type === selectedSummaryType) {
      setSelectedSummaryType(null)
    } else {
      setSelectedSummaryType(type)
    }
  }

  const getExceptionStatus = (type?: SUMMARY_TYPE | null) => {
    switch (type) {
      case SUMMARY_TYPE.DECISIONS_NEEDED:
        return 0
      case SUMMARY_TYPE.DECISIONED:
        return 1
      default:
        return null
    }
  }
  useEffect(() => {
    if (selectedSummaryType !== undefined) {
      handleSearch(defaultSearchData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSummaryType])

  useEffect(() => {
    if (previousSelectedAccounts !== selectedAccounts && !isFirstLoad) {
      handleSearch(defaultSearchData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccounts, defaultSearchData, isFirstLoad])

  // Report which services (ACH/Check) are enabled for the currently selected clients,
  // so the parent can hide/disable the tab of a service no selected client offers.
  useEffect(() => {
    if (!props.onAvailableServicesChange || !clients?.length) return
    const relevantClients = selectedClients?.includes(null)
      ? clients
      : clients.filter((client) => selectedClients.includes(client.id))
    if (!relevantClients.length) return
    props.onAvailableServicesChange({
      hasAch: relevantClients.some((client) => client.achEnabled),
      hasCheck: relevantClients.some((client) => client.checkEnabled),
    })
  }, [clients, selectedClients, props.onAvailableServicesChange])

  const generateCustomColumnDefs = useCallback(
    (columnDefs: Array<TableColumnDefs>): Array<TableColumnDefs> => {
      const defs = columnDefs.map((item: TableColumnDefs, index: any) => {
        const { field } = item
        const isNumberCol = numberColumns?.includes(field)
        const isDateCol = dateColumns?.includes(field)
        if (field === 'exceptionTypes')
          return {
            ...item,
            headerClass: 'custom-header-exceptionTypes',
            cellRenderer: (props: any) => {
              const { data } = props
              const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()
              const exceptionTypesArr = data?.exceptionTypes
                ?.split(',')
                ?.map((exceptionType: string) => exceptionType?.trim())
              return <ShortenListTags data={exceptionTypesArr} width={columnWidth} />
            },
          }

        if (field === 'defaultDecision')
          return {
            ...item,
            cellRenderer: (props: any) => {
              const { data } = props
              return data?.defaultDecision === null ? (
                <Typography />
              ) : (
                <DefaultDecisionTag status={data?.defaultDecision} />
              )
            },
            dataType: 'date' as any,
          }

        if (field === 'status')
          return {
            ...item,
            cellRenderer: (props: any) => {
              const { data } = props
              return data?.status === null ? <Typography /> : <StatusTag status={data?.status} />
            },
            dataType: 'date' as any,
          }

        if (field === 'reason')
          return {
            ...item,
            cellRenderer: (props: any) => {
              const { data } = props
              return data?.otherReason === null || data?.otherReason === '' ? data?.reason : data?.otherReason
            },
            dataType: 'date' as any,
          }

        if (field === 'amount' || field === 'issuedAmount')
          return {
            ...item,
            valueFormatter: (params: any) => (params.value !== null ? formatNumberWithCommas(params.value) : ''),
            minWidth: 150,
            dataType: 'number' as any,
          }
        return {
          ...item,
          dataType: isNumberCol ? 'number' : isDateCol ? 'date' : ('text' as any),
        }
      })

      return [
        {
          colId: 'transactionGroup',
          field: 'transactionGroup',
          headerName: '',
          hide: true,
          rowGroup: true,
          defaultSelect: false,
        },
        ...defs,
      ]
    },
    [dateColumns, numberColumns],
  )

  const onRowClicked = (row: any) => {
    // Click on row group header
    if (row?.node?.key === 'Decisioned' || row?.node?.key === 'Decisions Needed') {
      row.node.setExpanded(!row?.node?.expanded)
      return
    }
    // Click on record
    setExceptionDetail({
      id: row?.data?.id,
      type: row?.data?.exceptionTypeStrings,
    })

    setNavigationContext(row?.data?.status === 'EXCEPTION' ? 'EXCEPTION' : 'DECISIONED')

    setIsExceptionDetailModalOpen(true)
  }

  const handleCloseExceptionDetailModal = () => {
    setIsExceptionDetailModalOpen(false)
    setExceptionDetail(EXCEPTION_DETAIL_DEFAULT)
    setNavigationContext(null)
  }

  const [index, setIndex] = useState(-1)
  const [navigationContext, setNavigationContext] = useState<'EXCEPTION' | 'DECISIONED' | null>(null)
  const rowIdList = useMemo(() => rowData?.map((item: any) => item?.id), [rowData])

  const undecisionedItems = useMemo(() => {
    return rowData?.filter((item: any) => item?.status === 'EXCEPTION') || []
  }, [rowData])

  const decisionedItems = useMemo(() => {
    return rowData?.filter((item: any) => item?.status !== 'EXCEPTION') || []
  }, [rowData])

  const contextFilteredItems = useMemo(() => {
    if (navigationContext === 'EXCEPTION') return undecisionedItems
    if (navigationContext === 'DECISIONED') return decisionedItems
    return []
  }, [navigationContext, undecisionedItems, decisionedItems])

  const contextFilteredIdList = useMemo(() => {
    return contextFilteredItems?.map((item: any) => item?.id)
  }, [contextFilteredItems])

  useEffect(() => {
    setIndex(rowIdList?.findIndex((ele: string) => ele === exceptionDetail?.id))
  }, [rowIdList, exceptionDetail?.id, setIndex])

  const handleClickNext = () => {
    setTimeout(() => {
      const currentContextItems = navigationContext === 'EXCEPTION' 
        ? rowData?.filter((item: any) => item?.status === 'EXCEPTION') || []
        : rowData?.filter((item: any) => item?.status !== 'EXCEPTION') || []
      
      const currentContextIds = currentContextItems?.map((item: any) => item?.id) || []
      
      if (!currentContextItems?.length || currentContextItems?.length === 0) {
        handleCloseExceptionDetailModal()
        return
      }
      
      const currentContextIndex = currentContextIds?.findIndex((id: string) => id === exceptionDetail?.id)
      
      if (currentContextIndex === -1 && navigationContext === 'EXCEPTION') {
        const currentRowIndex = rowIdList?.findIndex((id: string) => id === exceptionDetail?.id)
        
        if (currentRowIndex !== -1) {
          const nextItemFromCurrent = rowData?.slice(currentRowIndex + 1)?.find((item: any) => item?.status === 'EXCEPTION')
          
          if (nextItemFromCurrent) {
            const nextIndexInRowData = rowIdList?.findIndex((id: string) => id === nextItemFromCurrent?.id)
            setIndex(nextIndexInRowData)
            setExceptionDetail({
              id: nextItemFromCurrent?.id,
              type: nextItemFromCurrent?.exceptionTypeStrings,
            })
            return
          }
        }
        
        const firstContextItem = currentContextItems?.[0]
        if (firstContextItem) {
          const firstIndexInRowData = rowIdList?.findIndex((id: string) => id === firstContextItem?.id)
          
          setIndex(firstIndexInRowData)
          setExceptionDetail({
            id: firstContextItem?.id,
            type: firstContextItem?.exceptionTypeStrings,
          })
          return
        }
        
        handleCloseExceptionDetailModal()
        return
      }
      
      if (currentContextIndex !== -1 && currentContextIndex < currentContextIds?.length - 1) {
        const nextContextItem = currentContextItems?.[currentContextIndex + 1]
        const nextIndexInRowData = rowIdList?.findIndex((id: string) => id === nextContextItem?.id)
        
        setIndex(nextIndexInRowData)
        setExceptionDetail({
          id: nextContextItem?.id,
          type: nextContextItem?.exceptionTypeStrings,
        })
        return
      }
      
      
      if (navigationContext === 'EXCEPTION' && currentContextIndex === currentContextIds?.length - 1) {
        if (currentContextItems?.length > 1) {
          const firstContextItem = currentContextItems?.[0]
          const firstIndexInRowData = rowIdList?.findIndex((id: string) => id === firstContextItem?.id)
          
          setIndex(firstIndexInRowData)
          setExceptionDetail({
            id: firstContextItem?.id,
            type: firstContextItem?.exceptionTypeStrings,
          })
          return
        }
        
        handleCloseExceptionDetailModal()
        return
      }
      
      if (navigationContext === 'DECISIONED') {
        handleCloseExceptionDetailModal()
        return
      }
      
      handleCloseExceptionDetailModal()
    }, 200)
  }

  const handleClickPrevious = () => {
    const currentContextIndex = contextFilteredIdList?.findIndex((id: string) => id === exceptionDetail?.id)
    
    if (currentContextIndex > 0) {
      const prevContextItem = contextFilteredItems?.[currentContextIndex - 1]
      const prevIndexInRowData = rowIdList?.findIndex((id: string) => id === prevContextItem?.id)
      
      setIndex(prevIndexInRowData)
      setExceptionDetail({
        id: prevContextItem?.id,
        type: prevContextItem?.exceptionTypeStrings,
      })
      return
    }
  }

  const [decisionModalOpen, setDecistionModalOpen] = useState(false)

  const [decision, setDecision] = useState(DECISION_TYPE.PAY)
  const handleCloseDecisionModal = () => {
    setIsPaying(false)
    setIsReturning(false)
    setDecistionModalOpen(false)
  }

  const handlePay = async () => {
    setIsPaying(true)
    const payload = {
      id: exceptionDetail?.id,
      decision: DECISION_CODE.PAY,
      reason: '',
      otherReason: '',
    }
    
    try {
      const res = exceptionDetail?.type?.includes('ACH') 
        ? await makeAchExceptionDecision(payload)
        : await makeCheckExceptionDecision(payload)
        
      if (res?.statusCode === STATUS_CODES.SUCCESS) {
        api.success({
          message: t(
            'SystemMessages:' +
            (exceptionDetail?.type?.includes('ACH')
              ? SYSTEM_MESSAGE_IDS.DECISION_ACH_MADE_SUCCESSFULLY
              : SYSTEM_MESSAGE_IDS.DECISION_CHECK_MADE_SUCCESSFULLY),
          ),
        })
        await outerTableRef.current?.handleSearchLogic?.({})
        await refreshSummaryAndBadges()
        setIsPaying(false)
        handleClickNext()
      }
    } finally {
      setIsPaying(false)
    }
  }

  const handleReturn = async () => {
    setIsReturning(true)
    if (exceptionDetail?.type?.includes('ACH')) {
      const payload = {
        id: exceptionDetail?.id,
        decision: DECISION_CODE.RETURN,
        reason: 'UNAUTH',
        otherReason: '',
      }
      
      try {
        const res = await makeAchExceptionDecision(payload)
        
        if (res?.statusCode === STATUS_CODES.SUCCESS) {
          api.success({
            message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.DECISION_ACH_MADE_SUCCESSFULLY),
          })
          await outerTableRef.current?.handleSearchLogic?.({})
          await refreshSummaryAndBadges()
          setIsReturning(false)
          handleClickNext()
        }
      } catch (error: any) {
        api.error({
          message: `Make decision Failed`,
          description: `${error}`,
          placement: 'topRight',
        })
      } 
      return
    }
    setDecision(DECISION_TYPE.RETURN)
    setDecistionModalOpen(true)
  }

  const getTableName = useCallback(() => {
    switch (props.type) {
      case QuickExceptionSetTabs.Ach:
        return TABLE_NAME.QUICK_EXCEPTION_PROCESSING_ACH
      case QuickExceptionSetTabs.Check:
        return TABLE_NAME.QUICK_EXCEPTION_PROCESSING_CHECK
      default:
        throw new Error('Invalid Quick Exception Set Tab Type')
    }
  }, [props.type])

  return (
    <Layout.Content className='quick-exception-container'>
      {contextHolder}
      <Row gutter={24} className='quick-exception-landing'>
        <Col span={24} className='quick-exception'>
          <Row gutter={24}>
            <SearchArea
              selectedClients={selectedClients}
              selectedAccounts={selectedAccounts}
              setSelectedClients={setSelectedClients}
              setSelectedAccounts={setSelectedAccounts}
              handleGetAccounts={handleGetAccounts}
              handleGetClients={handleGetClients}
              handleGetClientInfoByUser={handleGetClientInfoByUser}
              loadingAccounts={loadingAccounts}
              loadingClients={loadingClients}
              clients={clients}
              accounts={accounts}
            />
          </Row>
          {combinedSummaryInfo ? (
            <Row gutter={[24, 24]} wrap className='cover-summary-card'>
              {Object.values(SUMMARY_TYPE)?.map((item: SUMMARY_TYPE) => (
                <Col xs={{ span: 24 }} sm={{ span: 12 }} lg={{ span: 8 }} key={`summary-type-${item}`}>
                  <SummaryCard
                    summaryType={item}
                    summaryInfo={combinedSummaryInfo}
                    onClick={isLoading ? undefined : handleExceptionStatusSearch}
                    isSelected={item === selectedSummaryType}
                    tab={props.type}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <Skeleton />
          )}
          {selectedClients?.length && selectedAccounts?.length ? (
            <Row gutter={24} style={{ flex: 1 }}>
              <Col span={24}>
                <NewCommonTable
                  ref={outerTableRef}
                  pageName={getTableName()}
                  tableSettingName={getTableName()}
                  tableName={t('QuickExceptionProcessing:label.decisions')}
                  translationNamespace='QuickExceptionProcessing:table'
                  displayAddBtn={false}
                  displayPagination={false}
                  displayDownloadBtn={userPermissions[ESubModules.EXCEPT_PROCESS]?.includes(EPermissions.VIEW_ITEMS)}
                  displayActionColumn={false}
                  loading={isLoading}
                  rowData={rowData}
                  total={total}
                  handleSearch={handleSearch}
                  handleDownload={handleDownloadFunc}
                  tableContainerStyle={{
                    flex: 1,
                  }}
                  rootContainerStyle={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  groupDisplayType={'groupRows'}
                  suppressAggFuncInHeader
                  rowSelection='single'
                  onRowClicked={onRowClicked}
                  generateCustomColumnDefs={generateCustomColumnDefs}
                  groupDefaultExpanded={1}
                />
              </Col>
            </Row>
          ) : (
            <></>
          )}
        </Col>
      </Row>
      <ViewExceptionDetailModal
        isOpen={isExceptionDetailModalOpen}
        onClose={handleCloseExceptionDetailModal}
        loading={false}
        exceptionType={exceptionDetail?.type}
        selectedId={exceptionDetail?.id}
        handleClickNext={handleClickNext}
        handleClickPrevious={handleClickPrevious}
        selectedIdIndex={index}
        handlePay={handlePay}
        handleReturn={handleReturn}
        api={api}
        isPaying={isPaying}
        isReturning={isReturning}
        contextFilteredIdList={contextFilteredIdList}
        navigationContext={navigationContext}
      />

      <DecisionModal
        title={t(`QuickExceptionProcessing:label.${decision === DECISION_TYPE.RETURN ? 'return' : 'pay'}`)}
        titleDecision={t(`QuickExceptionProcessing:label.${decision === DECISION_TYPE.RETURN ? 'return' : 'pay'}`)}
        onClose={handleCloseDecisionModal}
        isOpen={decisionModalOpen}
        footer={null}
        api={api}
        decisionCode={decision}
        transactionType={exceptionDetail?.type?.includes('ACH') ? TRANSACTION_TYPE.ACH : TRANSACTION_TYPE.CHECK}
        exceptionID={exceptionDetail?.id}
        handleSearch={handleSearch}
        callbackAfterMakingDecision={handleClickNext}
        isACH={exceptionDetail?.type?.includes('ACH')}
        outerTableRef={outerTableRef}
        refreshSummaryAndBadges={refreshSummaryAndBadges}
      />
    </Layout.Content>
  )
}

export default QuickExceptionProcessing
